import {
  buyPrice,
  carriedEntries,
  getItem,
  type InventoryState,
  TUTORIAL_BOUGHT,
  TUTORIAL_FUSED,
  TUTORIAL_GIVEN,
  TUTORIAL_RESOURCE,
} from '@shefira/shared';

import type { QuestEntry } from './quest-log.js';

/**
 * The camp's coach (ADR 0078): from the journal, the bag and the windows on
 * screen, the one thing to do next and what to light up for it. Nothing
 * here decides anything of the game: it reads what the server sent and
 * points at a button, the way a friend leaning over the shoulder would.
 */

/** What the coach needs of the journal. */
export interface CoachJournal {
  known: boolean;
  isDone(questId: string): boolean;
  entry(questId: string): QuestEntry | undefined;
}

/** The windows on screen that change the next click. */
export interface CoachScreen {
  dialog: boolean;
  merchant: 'buy' | 'sell' | null;
  inventory: boolean;
  fusion: boolean;
  forge: boolean;
}

export interface CoachFacts {
  journal: CoachJournal;
  inventory: InventoryState | undefined;
  screen: CoachScreen;
  touch: boolean;
}

/** One line to read and the elements to light up (CSS selectors); none lights nothing. */
export interface CoachStep {
  text: string;
  targets: string[];
}

const ACCEPT = '.quest-dialog__go';
const SERVICE = '.quest-dialog__service';
const BAG_BUTTON = '[data-action="inventory"]';
/** On a narrow screen the inventory shows one half at a time: the bag's tab while the doll shows. */
const BAG_PAGE = '.inventory--page-doll .inventory__page[data-page="bag"]';

const bagItem = (templateId: string): string =>
  `.inventory .item[data-from="bag"][data-template="${templateId}"]`;

/** Take or give back a quest: the dialogue's button while it is open, else the townsperson. */
function speak(screen: CoachScreen, inDialog: string, outside: string): CoachStep {
  return screen.dialog ? { text: inDialog, targets: [ACCEPT] } : { text: outside, targets: [] };
}

/** Put on what waits in the bag: open it, then the pieces themselves. */
function wear(facts: CoachFacts, templateIds: readonly string[]): CoachStep {
  if (!facts.screen.inventory) {
    return { text: 'Ouvre ton sac pour enfiler ce que tu viens d’obtenir.', targets: [BAG_BUTTON] };
  }
  return {
    text: facts.touch
      ? 'Touche chaque pièce qui brille dans ton sac, puis « Équiper ».'
      : 'Double-clique sur chaque pièce qui brille dans ton sac pour l’enfiler.',
    targets: [BAG_PAGE, ...templateIds.map(bagItem)],
  };
}

function held(inventory: InventoryState | undefined): { templateId: string; upgradeLevel: number }[] {
  if (inventory === undefined) return [];
  return [
    ...carriedEntries(inventory).map(({ item }) => item),
    ...Object.values(inventory.equipped),
  ];
}

function inBag(inventory: InventoryState | undefined, templateId: string): boolean {
  return inventory?.bag.some((entry) => entry.item.templateId === templateId) ?? false;
}

function worn(inventory: InventoryState | undefined, templateId: string): boolean {
  return Object.values(inventory?.equipped ?? {}).some((item) => item.templateId === templateId);
}

function hunt(facts: CoachFacts): CoachStep {
  const { journal, screen } = facts;
  const entry = journal.entry('camp-rats');
  if (entry === undefined) {
    return speak(
      screen,
      'Accepte la quête de Ser Givrecoeur.',
      'Clique sur Ser Givrecoeur, le chevalier juste à côté de toi.',
    );
  }
  if (!entry.complete) {
    return {
      text: 'Suis la flèche jusqu’aux rats et clique sur un rat pour l’attaquer. Clique ensuite sur ce qu’il laisse pour le ramasser.',
      targets: [],
    };
  }
  return speak(screen, 'Rends la quête à Ser Givrecoeur.', 'Retourne voir Ser Givrecoeur et clique sur lui.');
}

function armour(facts: CoachFacts): CoachStep {
  const { journal, screen, inventory } = facts;
  const entry = journal.entry('camp-armour');
  if (entry === undefined) {
    return speak(
      screen,
      'Accepte la quête d’Odon.',
      'Suis la flèche jusqu’à Odon, le marchand, et clique sur lui.',
    );
  }
  if (entry.complete) {
    return speak(screen, 'Rends la quête à Odon.', 'Retourne voir Odon et clique sur lui.');
  }
  // Odon's gifts are worn like the pieces bought; a gift not in the bag (a
  // lesson taken before he gave them) is bought like the rest.
  const missing = [...TUTORIAL_GIVEN, ...TUTORIAL_BOUGHT].filter((id) => !worn(inventory, id));
  const toBuy = missing.filter((id) => !inBag(inventory, id));
  const toWear = missing.filter((id) => inBag(inventory, id));
  const pelts = inBag(inventory, TUTORIAL_RESOURCE);
  const shop = (inShop: CoachStep): CoachStep => {
    if (screen.merchant !== null) return inShop;
    if (screen.dialog) return { text: 'Ouvre les affaires d’Odon.', targets: [SERVICE] };
    return { text: 'Clique sur Odon, le marchand.', targets: [] };
  };
  if (pelts && toBuy.length > 0) {
    return shop(
      screen.merchant === 'sell'
        ? {
            text: 'Vends tes peaux : « Tout » vend la pile entière.',
            targets: [`.merchant__row[data-template="${TUTORIAL_RESOURCE}"] .merchant__sell`],
          }
        : { text: 'Ouvre l’onglet Vendre pour vendre tes peaux.', targets: ['.merchant__tab[data-mode="sell"]'] },
    );
  }
  if (toBuy.length > 0) {
    const gold = inventory?.gold ?? 0;
    const cheapest = Math.min(...toBuy.map((id) => buyPriceOf(id)));
    if (gold < cheapest) {
      return {
        text: 'Il te manque de l’or : retourne chasser des rats et vends leurs peaux à Odon.',
        targets: [],
      };
    }
    return shop(
      screen.merchant === 'buy'
        ? {
            text: 'Achète les pièces qui brillent.',
            targets: toBuy.map((id) => `.merchant__row[data-template="${id}"] .merchant__sell`),
          }
        : { text: 'Ouvre l’onglet Acheter.', targets: ['.merchant__tab[data-mode="buy"]'] },
    );
  }
  return wear(facts, toWear);
}

function buyPriceOf(templateId: string): number {
  const template = getItem(templateId);
  return template === undefined ? 0 : buyPrice(template);
}

function lesson(facts: CoachFacts): CoachStep {
  const { journal, screen, inventory } = facts;
  const entry = journal.entry('camp-fusion');
  if (entry === undefined) {
    return speak(
      screen,
      'Accepte la quête de Garin.',
      'Suis la flèche jusqu’à Garin, le fondeur, et clique sur lui.',
    );
  }
  if (entry.complete) {
    return speak(screen, 'Rends la quête à Hald.', 'Retourne voir Hald, le forgeron, et clique sur lui.');
  }
  const swords = held(inventory).filter((item) => item.templateId === TUTORIAL_FUSED);
  if (swords.length === 0) {
    if (screen.fusion) {
      return { text: 'Clique sur « Fusionner ».', targets: ['.fusion .smith__go:not(.fusion__wear)'] };
    }
    if (screen.dialog) return { text: 'Ouvre l’atelier de Garin.', targets: [SERVICE] };
    return { text: 'Clique sur Garin pour fondre tes cinq épées.', targets: [] };
  }
  if (!worn(inventory, TUTORIAL_FUSED)) {
    if (screen.fusion) {
      return { text: 'Prends l’épée rare en main.', targets: ['.fusion__wear'] };
    }
    return wear(facts, [TUTORIAL_FUSED]);
  }
  if (screen.forge) {
    return {
      text: 'Choisis ton épée rare, puis clique sur « Améliorer ».',
      targets: [
        `[data-panel="forge"] .smith__piece[data-template="${TUTORIAL_FUSED}"]:not(.smith__piece--selected)`,
        '[data-panel="forge"] .smith__go',
      ],
    };
  }
  if (screen.dialog) return { text: 'Ouvre la forge de Hald.', targets: [SERVICE] };
  return { text: 'Suis la flèche jusqu’à Hald, le forgeron, et clique sur lui.', targets: [] };
}

function road(facts: CoachFacts): CoachStep | null {
  const { journal, screen } = facts;
  if (journal.isDone('camp-road')) return null;
  if (journal.entry('camp-road') === undefined) {
    return speak(
      screen,
      'Accepte la lettre de Ser Givrecoeur.',
      'Retourne voir Ser Givrecoeur, il a une lettre pour toi.',
    );
  }
  return { text: 'Passe l’arche bleue, à l’est, pour rejoindre Havrebourg.', targets: [] };
}

/**
 * The basic controls show from the arrival until the first rat falls: the
 * first lesson not handed in, and none of its rats counted yet.
 */
export function controlsDue(journal: CoachJournal): boolean {
  if (!journal.known || journal.isDone('camp-rats')) return false;
  const entry = journal.entry('camp-rats');
  if (entry === undefined) return true;
  return !entry.complete && entry.progress.every((objective) => objective.value === 0);
}

export function coachStep(facts: CoachFacts): CoachStep | null {
  const { journal } = facts;
  if (!journal.known) return null;
  if (!journal.isDone('camp-rats')) return hunt(facts);
  if (!journal.isDone('camp-armour')) return armour(facts);
  if (!journal.isDone('camp-fusion')) return lesson(facts);
  return road(facts);
}
